/**
 * Single source of truth for the admin navigation IA.
 * See: tasks/admin-ia-plan.md
 */

export type NavGroupKey =
  | "tenants"
  | "books"
  | "freight"
  | "system";

export interface SubTab {
  label: string;
  href: string;
}

export interface NavGroup {
  key: NavGroupKey;
  label: string;
  /** Root path for this group (used by the Topbar link). */
  href: string;
  /**
   * Predicate: is the given pathname inside this group?
   * Tenants is special — it's the catch-all for top-level admin routes.
   */
  match: (pathname: string) => boolean;
  tabs: SubTab[];
}

export const NAV_GROUPS: NavGroup[] = [
  {
    key: "tenants",
    label: "Tenants",
    href: "/",
    match: (p) => p === "/" || p.startsWith("/tenants"),
    tabs: [
      { label: "Dashboard", href: "/" },
      { label: "Organizations", href: "/tenants/organizations" },
      { label: "Users", href: "/tenants/users" },
      { label: "Vehicles", href: "/tenants/vehicles" },
      { label: "Drivers", href: "/tenants/drivers" },
      { label: "Carriers", href: "/tenants/carriers" },
      { label: "Trips", href: "/tenants/trips" },
      { label: "Broadcasts", href: "/tenants/broadcasts" },
      { label: "Reference data", href: "/tenants/reference" },
      { label: "Goods catalog", href: "/tenants/goods" },
    ],
  },
  {
    key: "books",
    label: "Books",
    href: "/books",
    match: (p) => p.startsWith("/books"),
    tabs: [
      { label: "Dashboard", href: "/books" },
      { label: "Invoices", href: "/books/invoices" },
      { label: "Bills", href: "/books/bills" },
      { label: "Expenses", href: "/books/expenses" },
      { label: "Contacts", href: "/books/contacts" },
      { label: "Chart of accounts", href: "/books/coa" },
      { label: "Ledger", href: "/books/ledger" },
      { label: "Banking", href: "/books/banking" },
      { label: "Reports", href: "/books/reports" },
      { label: "Engine settings", href: "/books/settings" },
    ],
  },
  {
    key: "freight",
    label: "Freight Board",
    href: "/freight/loads",
    match: (p) => p.startsWith("/freight"),
    tabs: [
      { label: "Loads", href: "/freight/loads" },
      { label: "Bids", href: "/freight/bids" },
      { label: "Lanes", href: "/freight/lanes" },
      { label: "Shippers", href: "/freight/shippers" },
      { label: "Carrier onboarding", href: "/freight/onboarding" },
    ],
  },
  {
    key: "system",
    label: "System",
    href: "/system/health",
    match: (p) => p.startsWith("/system"),
    tabs: [
      { label: "Health", href: "/system/health" },
      { label: "Integrations", href: "/system/integrations" },
      { label: "Webhooks", href: "/system/webhooks" },
      { label: "Jobs & queues", href: "/system/jobs" },
      { label: "Error reports", href: "/system/error-reports" },
      { label: "Feature flags", href: "/system/flags" },
      { label: "API keys", href: "/system/api-keys" },
      { label: "Classifiers", href: "/system/classifiers" },
    ],
  },
];

/**
 * Find the active nav group for a given pathname. Falls back to Tenants.
 */
export function findActiveGroup(pathname: string): NavGroup {
  // Non-tenants groups first (they have stricter prefixes), then Tenants as fallback.
  for (const g of NAV_GROUPS) {
    if (g.key !== "tenants" && g.match(pathname)) return g;
  }
  return NAV_GROUPS[0];
}

/**
 * Find which sub-tab in a group is active for a given pathname.
 * Picks the longest matching href.
 */
export function findActiveTabHref(
  group: NavGroup,
  pathname: string,
): string | null {
  let best: SubTab | null = null;
  for (const t of group.tabs) {
    if (pathname === t.href || pathname.startsWith(`${t.href}/`)) {
      if (!best || t.href.length > best.href.length) best = t;
    }
  }
  return best?.href ?? null;
}
